import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type HourBankRow, type MessagePurpose, type MessageTemplate } from '../api';
import { ActiveBadge, Dialog, ErrorAlert, Field, Loading } from '../components/ui';
import { todayIso } from '../format';
import { MessagesList, PrepareMessageButton, PURPOSE_LABEL } from './Mensagens';
import { IndicatorsPanel } from './Indicadores';

// Relatórios (seção 12): extratos para o cliente e acompanhamento das mensagens. Indicadores e relatórios gerenciais: etapa E6.
export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as 'extratos' | 'mensagens' | 'gerenciais') || 'extratos';
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Relatórios</h1>
          <p>Documentos para o cliente e mensagens. Gerar ou baixar um documento não envia nada nem altera faturamento.</p>
        </div>
      </div>
      <div className="segmented" role="tablist" style={{ marginBottom: '1rem' }}>
        <button role="tab" aria-pressed={tab === 'extratos'} onClick={() => setParams({ tab: 'extratos' })}>
          Extratos de banco
        </button>
        <button role="tab" aria-pressed={tab === 'mensagens'} onClick={() => setParams({ tab: 'mensagens' })}>
          Mensagens
        </button>
        <button role="tab" aria-pressed={tab === 'gerenciais'} onClick={() => setParams({ tab: 'gerenciais' })}>
          Indicadores e gerenciais
        </button>
      </div>
      {tab === 'extratos' && <StatementsTab />}
      {tab === 'mensagens' && <MessagesList />}
      {tab === 'gerenciais' && (
        <>
          <IndicatorsPanel />
          <p className="small muted">
            O relatório da sessão fica na página de cada execução confirmada. A exportação financeira (CSV de transferência) fica em <Link to="/faturamento">Faturamento</Link>.
          </p>
        </>
      )}
    </>
  );
}

export function StatementLinks({ contractId, from, to }: { contractId: string; from: string; to: string }) {
  const query = qs({ from: from || undefined, to });
  return (
    <div className="actions">
      <a className="btn btn-sm" href={`/api/v1/reports/statement/${contractId}/pdf${query}`} target="_blank" rel="noopener">
        PDF
      </a>
      <a className="btn btn-sm" href={`/api/v1/reports/statement/${contractId}/html${query}`} target="_blank" rel="noopener">
        HTML
      </a>
      <a className="btn btn-sm" href={`/api/v1/reports/statement/${contractId}/xlsx${query}`}>
        Excel
      </a>
      <a className="btn btn-sm" href={`/api/v1/reports/statement/${contractId}/csv${qs({ from: from || undefined, to, csv: 'br' })}`}>
        CSV
      </a>
      <a className="btn btn-sm btn-ghost" href={`/api/v1/reports/statement/${contractId}/csv${qs({ from: from || undefined, to, csv: 'canonico' })}`}>
        CSV canônico
      </a>
      <PrepareMessageButton label="Preparar envio ao cliente" className="btn btn-sm btn-primary" body={{ purpose: 'extrato', contractId, periodFrom: from || null, periodTo: to }} />
    </div>
  );
}

function StatementsTab() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(todayIso());
  const q = useQuery({ queryKey: ['hour-banks', 'all'], queryFn: () => api.get<{ data: HourBankRow[] }>('/hour-banks?scope=all') });
  return (
    <>
      <div className="toolbar">
        <label htmlFor="st-from" className="small">
          De
        </label>
        <input id="st-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ flex: '0 1 180px' }} />
        <label htmlFor="st-to" className="small">
          Até (data de corte)
        </label>
        <input id="st-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ flex: '0 1 180px' }} />
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="list">
          {q.data?.data.map((b) => (
            <div key={b.id} className="list-item">
              <div className="title">
                {b.clientName} · {b.projectName} <span className="badge">{b.code}</span>
              </div>
              {b.hasInitialCredit ? <StatementLinks contractId={b.id} from={from} to={to} /> : <div className="small muted">Crédito inicial não registrado.</div>}
            </div>
          ))}
        </div>
      )}
      <p className="small muted">O documento do cliente mostra créditos, consumos, estornos, ajustes e saldo na data de corte — sem preços internos nem dados de outros clientes.</p>
    </>
  );
}

// ------------------------------------------------------------------ modelos de mensagem (Cadastros)

export function TemplatesPage() {
  const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);
  const q = useQuery({ queryKey: ['message-templates'], queryFn: () => api.get<{ data: MessageTemplate[]; allowedVariables: Record<MessagePurpose, string[]> }>('/message-templates') });
  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/cadastros">← Cadastros</Link>
          </p>
          <h1>Modelos de mensagem</h1>
          <p>Use variáveis como {'{{contato}}'}. Mensagens com variável vazia não podem ser enviadas.</p>
        </div>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            Novo modelo
          </button>
        </div>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="list">
          {q.data?.data.map((tpl) => (
            <button key={tpl.id} className="list-item" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }} onClick={() => setEditing(tpl)}>
              <div className="title">
                {tpl.name} <span className="badge">{PURPOSE_LABEL[tpl.purpose]}</span> {tpl.isDefault && <span className="badge badge-brand">padrão</span>} <ActiveBadge active={tpl.active} />{' '}
                <span className="small muted">v{tpl.version}</span>
              </div>
              <div className="meta">{tpl.subject}</div>
            </button>
          ))}
        </div>
      )}
      {editing && q.data && <TemplateDialog template={editing === 'new' ? null : editing} allowed={q.data.allowedVariables} onClose={() => setEditing(null)} />}
    </>
  );
}

function TemplateDialog({ template, allowed, onClose }: { template: MessageTemplate | null; allowed: Record<MessagePurpose, string[]>; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    purpose: template?.purpose ?? ('lembrete' as MessagePurpose),
    name: template?.name ?? '',
    subject: template?.subject ?? '',
    body: template?.body ?? '',
    active: template?.active ?? true,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const m = useMutation({
    mutationFn: () => (template ? api.put(`/message-templates/${template.id}`, { ...form, version: template.version }) : api.post('/message-templates', form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['message-templates'] });
      onClose();
    },
  });
  return (
    <Dialog title={template ? 'Editar modelo' : 'Novo modelo'} onClose={onClose}>
      <div className="form">
        <ErrorAlert error={m.error} />
        <Field label="Finalidade">
          {(p) => (
            <select {...p} value={form.purpose} disabled={Boolean(template)} onChange={(e) => set('purpose', e.target.value as MessagePurpose)}>
              {(Object.keys(PURPOSE_LABEL) as MessagePurpose[]).map((k) => (
                <option key={k} value={k}>
                  {PURPOSE_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Nome" required>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <Field label="Assunto" required>
          {(p) => <input {...p} value={form.subject} onChange={(e) => set('subject', e.target.value)} />}
        </Field>
        <Field label="Texto" required hint={`Variáveis permitidas: ${allowed[form.purpose].map((v) => `{{${v}}}`).join(' ')}`}>
          {(p) => <textarea {...p} rows={10} value={form.body} onChange={(e) => set('body', e.target.value)} />}
        </Field>
        <label className="check">
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Ativo
        </label>
        {template && <p className="small muted">Salvar cria a versão {template.version + 1}. Mensagens já preparadas mantêm o texto da versão usada.</p>}
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending} onClick={() => m.mutate()}>
            Salvar
          </button>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </Dialog>
  );
}
